/* eslint-disable prefer-rest-params */
import { LogLevel } from '../types';
import { filterReporterMetadata } from '../utils';
import { consoleTransport } from './consoleTransport';
import { getLogMessage, isAboveLevel } from './utils';
export * from './consoleTransport';
export * from './datadogTransport';
export const globalMetadata = {};
export const logTransports = [consoleTransport()];
export let globalLogLevel;
/**
 * Sets the global log level.
 */
export function setLogLevel(level) {
    globalLogLevel = level ?? undefined;
}
/**
 * Creates a Logger Instance.
 */
export function createLogger(options = {}) {
    const rootMetadata = options.metadata ?? {};
    const logger = {
        // impl loosely based on Winston Logger's dynamic log function
        // https://github.com/winstonjs/winston/blob/master/lib/winston/logger.js
        log() {
            let logInfo;
            // Optimize for the hotpath of logging JSON literals
            if (arguments.length === 1) {
                logInfo = {
                    level: LogLevel.Info,
                    ...arguments[0],
                };
            }
            // Slightly less hotpath, but worth optimizing for.
            else if (arguments.length === 2) {
                const [level, message] = Array.from(arguments);
                if (message && typeof message === 'object') {
                    logInfo = {
                        ...message,
                        level: message.level ?? level,
                    };
                }
                else {
                    logInfo = {
                        level,
                        message,
                    };
                }
            }
            else {
                const [level, message, ...splat] = Array.from(arguments);
                const [meta] = splat;
                if (typeof meta === 'object' && meta !== null) {
                    logInfo = {
                        ...meta,
                        level,
                        splat: splat.slice(1),
                        message,
                    };
                    if (meta.message) {
                        logInfo.message = `${getLogMessage(logInfo.message)} ${getLogMessage(meta.message)}`;
                    }
                    if (meta.stack) {
                        logInfo.stack = meta.stack;
                    }
                }
                else {
                    logInfo = {
                        level,
                        splat,
                        message,
                    };
                }
            }
            // Check if we should log this message
            const minLevel = globalLogLevel ?? options.logLevel;
            if (minLevel && !isAboveLevel(logInfo.level, minLevel)) {
                return logger;
            }
            // Add timestamp and root metadata.
            // Ensure there is a message property.
            logInfo = {
                ...globalMetadata,
                ...rootMetadata,
                timestamp: new Date().toISOString(),
                ...logInfo,
                message: logInfo.message ?? '',
            };
            for (const transport of logTransports) {
                if (transport.logLevel) {
                    if (!isAboveLevel(logInfo.level, transport.logLevel)) {
                        continue;
                    }
                }
                transport.log({
                    ...filterReporterMetadata(logInfo, transport),
                    // These would break the transport if excluded.
                    level: logInfo.level,
                    message: logInfo.message,
                    splat: logInfo.splat,
                });
            }
            return logger;
        },
        child(metadata) {
            return createLogger({ ...rootMetadata, metadata });
        },
        startTimer() {
            const start = new Date();
            return {
                logger: this,
                done: ({ message, level, metadata } = {}) => {
                    const duration = new Date().getTime() - start.getTime();
                    logger.log(level ?? LogLevel.Info, message ?? 'Timer Completed', { duration, ...(metadata ?? {}) });
                    return true;
                },
            };
        },
        // Forward the log levels into the log function, essentially shortcuts.
        debug: (...args) => logger.log(LogLevel.Debug, ...args),
        info: (...args) => logger.log(LogLevel.Info, ...args),
        warn: (...args) => logger.log(LogLevel.Warn, ...args),
        error: (...args) => logger.log(LogLevel.Error, ...args),
    };
    return logger;
}
export const logger = createLogger();
//# sourceMappingURL=index.js.map